import React, { useMemo } from 'react';
import { CheckCircle2, Circle, Clock, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * "Your Progress" — a 6-step checklist tracking where the client is in the
 * All Build Consult workflow. Reads from the client's bookings (most recent
 * non-deleted) and surfaces the same status on both the Dashboard and the
 * Consultants page so users always know what comes next.
 *
 * Props:
 *   bookings: Booking[]  (current user's bookings; deleted ones are filtered)
 *   variant?: 'card' | 'banner'   compact card (Dashboard) vs. wide banner (Consultants)
 */
const STEPS = [
  {
    key: 'pre_assessment',
    title: 'Pre-assessment booking',
    blurb: 'You submit your pre-assessment with all relevant project data.',
  },
  {
    key: 'team_review',
    title: 'Team review & response',
    blurb: 'All Build Consult reviews and confirms whether we can help.',
  },
  {
    key: 'deposit_requested',
    title: 'Initial deposit requested',
    blurb: 'A deposit is requested as your acceptance of our services.',
  },
  {
    key: 'report_processing',
    title: 'Report gathered & reviewed',
    blurb: 'Our team gathers, processes and quality-checks your report.',
  },
  {
    key: 'report_issued',
    title: 'Report issued',
    blurb: 'A copy of your report and recommendations is issued.',
  },
  {
    key: 'feedback',
    title: 'Feedback & clarification',
    blurb: 'We check on your feedback and answer any clarification questions.',
  },
];

/**
 * Map a list of bookings to a {stepKey: 'done'|'current'|'todo'} object.
 *
 * Rules — uses the most progressed booking across all of the user's active
 * bookings so a user with multiple jobs sees their furthest-advanced state.
 */
function computeStepStatuses(bookings = []) {
  const active = (bookings || []).filter((b) => !b.deleted_at);

  // Each rule is a predicate against the booking that returns true if that
  // step is "done". We OR them across all active bookings.
  const has = (predicate) => active.some(predicate);

  const stage = (b) => (b.stage || '').toLowerCase();
  const isPaid = (b) => (b.payment_status || '').toLowerCase() === 'paid' || !!b.paid_at;

  const done = {
    pre_assessment: active.length > 0,
    team_review: has((b) =>
      ['reviewed', 'approved', 'in_progress', 'awaiting_payment', 'paid', 'completed']
        .includes((b.status || '').toLowerCase())
      || (b.deposit_amount || 0) > 0
      || isPaid(b)
      || ['deposit_paid', 'report_issued', 'next_stage_confirmed', 'next_stage_booked', 'next_stage_paid', 'completed'].includes(stage(b))
    ),
    deposit_requested: has((b) =>
      isPaid(b) || ['deposit_paid', 'report_issued', 'next_stage_confirmed', 'next_stage_booked', 'next_stage_paid', 'completed'].includes(stage(b))
    ),
    report_processing: has((b) =>
      !!b.report_eta
      || !!b.report_pdf_path
      || ['report_issued', 'next_stage_confirmed', 'next_stage_booked', 'next_stage_paid', 'completed'].includes(stage(b))
    ),
    report_issued: has((b) =>
      !!b.report_pdf_path
      || ['report_issued', 'next_stage_confirmed', 'next_stage_booked', 'next_stage_paid', 'completed'].includes(stage(b))
    ),
    feedback: has((b) =>
      ['next_stage_confirmed', 'next_stage_booked', 'next_stage_paid', 'completed'].includes(stage(b))
    ),
  };

  // Compute current step = first step not marked done. Earlier steps are done,
  // current is in progress (Clock icon), later steps are todo.
  const order = STEPS.map((s) => s.key);
  let currentIdx = order.findIndex((k) => !done[k]);
  if (currentIdx === -1) currentIdx = order.length; // all done

  const status = {};
  order.forEach((k, idx) => {
    if (done[k]) status[k] = 'done';
    else if (idx === currentIdx) status[k] = 'current';
    else status[k] = 'todo';
  });
  return { status, currentIdx };
}

const STATUS_STYLES = {
  done: {
    icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" />,
    text: 'text-slate-900',
    pill: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    label: 'Done',
  },
  current: {
    icon: <Clock className="w-5 h-5 text-blue-700" />,
    text: 'text-slate-900',
    pill: 'bg-blue-100 text-blue-800 border-blue-200',
    label: 'In progress',
  },
  todo: {
    icon: <Circle className="w-5 h-5 text-slate-300" />,
    text: 'text-slate-500',
    pill: 'bg-slate-100 text-slate-500 border-slate-200',
    label: 'Up next',
  },
};

export default function ProgressChecklist({ bookings = [], variant = 'card' }) {
  const navigate = useNavigate();
  const { status, currentIdx } = useMemo(() => computeStepStatuses(bookings), [bookings]);
  const completedCount = STEPS.filter((s) => status[s.key] === 'done').length;
  const allDone = completedCount === STEPS.length;
  const noBookings = (bookings || []).filter((b) => !b.deleted_at).length === 0;

  // Map the current step to a helpful CTA so users always know what to do next.
  const nextCta = (() => {
    if (allDone) return null;
    const stepKey = STEPS[currentIdx]?.key;
    switch (stepKey) {
      case 'pre_assessment':
        return { label: 'Start pre-assessment', to: '/bookings' };
      case 'team_review':
        return { label: 'View Build Enquiries', to: '/dashboard' };
      case 'deposit_requested':
        return { label: 'Open Payments', to: '/payments' };
      case 'report_processing':
        return { label: 'Track Reports', to: '/reports' };
      case 'report_issued':
        return { label: 'View Reports', to: '/reports' };
      case 'feedback':
        return { label: 'Leave Feedback', to: '/feedback' };
      default:
        return null;
    }
  })();

  const headerCopy = noBookings
    ? 'Your journey starts with a pre-assessment.'
    : allDone
    ? 'All six steps completed. Thank you for working with us.'
    : 'Here\'s where you are in the All Build Consult workflow:';

  return (
    <section
      data-testid="progress-checklist"
      data-completed-count={completedCount}
      className={`grid-border bg-white ${variant === 'banner' ? 'p-6 md:p-7' : 'p-5'}`}
    >
      <header className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3
            className="text-lg font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            Your Progress
          </h3>
          <p className="text-sm text-slate-600 mt-1">{headerCopy}</p>
        </div>
        <div className="flex items-center gap-3">
          <span
            data-testid="progress-checklist-counter"
            className="text-xs font-semibold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 px-2 py-1 rounded-sm"
          >
            {completedCount}/{STEPS.length} complete
          </span>
          {nextCta && (
            <button
              type="button"
              onClick={() => navigate(nextCta.to)}
              data-testid="progress-checklist-cta"
              className="hidden md:inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 bg-slate-900 text-white rounded-sm hover:bg-black"
            >
              {nextCta.label}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </header>

      <ol className={`space-y-3 ${variant === 'banner' ? 'md:grid md:grid-cols-2 md:gap-x-6 md:gap-y-3 md:space-y-0' : ''}`}>
        {STEPS.map((step, idx) => {
          const s = STATUS_STYLES[status[step.key]];
          const isLast = idx === STEPS.length - 1;
          return (
            <li
              key={step.key}
              data-testid={`progress-step-${step.key}`}
              data-status={status[step.key]}
              className={`flex items-start gap-3 ${variant === 'banner' ? '' : 'relative'}`}
            >
              <div className="flex flex-col items-center flex-shrink-0">
                <div className="flex items-center justify-center w-7 h-7 rounded-full bg-white border border-slate-200">
                  {s.icon}
                </div>
                {!isLast && variant !== 'banner' && (
                  <div className="w-px flex-1 bg-slate-200 mt-1 mb-1" style={{ minHeight: 18 }} />
                )}
              </div>
              <div className="min-w-0 flex-1 pb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-sm font-semibold ${s.text}`}>
                    {idx + 1}. {step.title}
                  </span>
                  <span
                    className={`text-[10px] uppercase font-bold tracking-wider border px-1.5 py-0.5 rounded-sm ${s.pill}`}
                  >
                    {s.label}
                  </span>
                </div>
                <p className={`text-xs mt-0.5 ${status[step.key] === 'todo' ? 'text-slate-400' : 'text-slate-600'}`}>
                  {step.blurb}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      {nextCta && (
        <div className="mt-4 md:hidden">
          <button
            type="button"
            onClick={() => navigate(nextCta.to)}
            className="w-full inline-flex items-center justify-center gap-1 text-xs font-semibold px-3 py-2 bg-slate-900 text-white rounded-sm hover:bg-black"
          >
            {nextCta.label}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </section>
  );
}
